import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Home, Phone, CheckCircle2, ShieldCheck } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { company } from "../mock";

const INCLUDED = [
  "Assistance with bathing, dressing and grooming",
  "Meal preparation and feeding assistance",
  "Light housekeeping, laundry and home safety",
  "Medication reminders",
  "Mobility and transfer support",
  "Community participation and errands",
];

const FAQS = [
  { q: "What is HPC (Homemaker Personal Care) in Ohio?", a: "HPC — Homemaker Personal Care — is a DODD waiver service that combines hands-on personal care with homemaker support so individuals with developmental disabilities can live safely at home and participate in their community. It is authorized through the individual's service plan with their County Board of Developmental Disabilities." },
  { q: "Who qualifies for HPC services?", a: "HPC is available to individuals enrolled on an Ohio DODD waiver (such as the Individual Options, Level One or SELF waiver) whose service plan includes HPC. Your county board Service and Support Administrator (SSA) determines eligibility and authorized hours." },
  { q: "How do I start HPC services with Caregivers Alliance?", a: "Contact us at (614) 526-1548 or submit a referral online. We coordinate with your SSA and county board to confirm the service plan and authorization before care begins." },
  { q: "Can a family member be paid to provide HPC?", a: "Under some Ohio participant-directed options, a qualified family member or friend may provide approved services. Eligibility depends on the waiver, service plan and program rules — ask us about the family caregiver pathway." },
];

export default function HpcServices() {
  useSeo({
    title: "HPC Services in Ohio | Homemaker Personal Care (DODD Waiver) | Caregivers Alliance Homecare",
    description: "Homemaker Personal Care (HPC) services in Ohio from a DODD-certified provider. Personal care, homemaker support and community participation for individuals on DODD waivers. Now accepting new clients. Call (614) 526-1548.",
    keywords: "HPC services Ohio, homemaker personal care Ohio, HPC provider Columbus Ohio, DODD waiver HPC, HPC provider near me Ohio, county board HPC provider",
    path: "/hpc",
  });
  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <Home className="h-4 w-4" /> DODD Waiver Service
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl max-w-3xl">Homemaker Personal Care (HPC) in Ohio</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Person-centered HPC support from a DODD-certified provider — now accepting new clients.
            We coordinate with County Boards and SSAs across Ohio.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/refer"><Button data-testid="hpc-refer-btn" className="rounded-full bg-white text-[#0E5A72] hover:bg-white/90 h-12 px-7 font-semibold">REFER A CLIENT</Button></Link>
            <Link to="/contact"><Button data-testid="hpc-request-btn" className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white h-12 px-7 font-semibold">REQUEST HOME CARE</Button></Link>
            <a href={`tel:${company.phoneRaw}`}><Button className="rounded-full bg-white/15 hover:bg-white/25 text-white h-12 px-7 font-semibold border border-white/30"><Phone className="mr-2 h-4 w-4" /> {company.phone}</Button></a>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display font-bold text-3xl text-[#0E2A33]">What HPC support can include</h2>
            <div className="mt-6 space-y-3">
              {INCLUDED.map((s) => (
                <div key={s} className="flex items-center gap-3 bg-[#F7FBFC] rounded-xl border border-slate-100 p-4">
                  <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] shrink-0" />
                  <span className="text-slate-700 font-medium">{s}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-5">
              Services are authorized through the individual's service plan. Eligibility, authorization and
              program participation are verified before services begin.
            </p>
          </div>
          <div className="bg-[#F7FBFC] rounded-2xl border border-slate-100 p-8">
            <div className="flex items-center gap-2 text-[#0E5A72] font-semibold mb-3"><ShieldCheck className="h-5 w-5" /> How HPC gets started</div>
            <ol className="space-y-4 text-slate-700 text-sm">
              <li><span className="font-semibold text-[#0E2A33]">1. Talk to your SSA.</span> Your county board Service and Support Administrator confirms HPC is in the service plan.</li>
              <li><span className="font-semibold text-[#0E2A33]">2. Send us a referral.</span> Families, SSAs and county-board teams can call or use our online referral form.</li>
              <li><span className="font-semibold text-[#0E2A33]">3. We verify and match.</span> We confirm authorization, discuss scheduling and match a qualified caregiver.</li>
              <li><span className="font-semibold text-[#0E2A33]">4. Care begins.</span> Person-centered support with ongoing communication with your team.</li>
            </ol>
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/refer"><Button className="w-full rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white h-12 font-semibold">SEND A REFERRAL</Button></Link>
              <Link to="/family-caregiver"><Button variant="outline" className="w-full rounded-full border-[#0E5A72] text-[#0E5A72] h-12 font-semibold">Ask about family caregiver options</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-[#F7FBFC]">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33] text-center">HPC in Ohio — Frequently Asked Questions</h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="font-semibold text-[#0E2A33]">{f.q}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 text-sm text-slate-600">
            Related: <Link to="/dodd" className="text-[#0E5A72] font-medium hover:underline">DODD Services</Link> ·{" "}
            <Link to="/respite-care" className="text-[#0E5A72] font-medium hover:underline">Respite Care</Link> ·{" "}
            <Link to="/how-to-get-home-care-in-ohio" className="text-[#0E5A72] font-medium hover:underline">How to Get Home Care in Ohio</Link>
          </div>
        </div>
      </section>
    </>
  );
}
